 import React, {useEffect, useState} from 'react';
import {LoaderFunctionArgs, useLoaderData, useNavigate, useParams} from "react-router-dom";
import {Button, Grid, Hidden, Modal, Paper, Stack, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import {addItemToPreReg} from "./MagicItems";
import Box from "@mui/material/Box";
import axios from "axios";
import {Adb, Construction, DirectionsCar, FlashOn, LocalDining, PointOfSale} from "@mui/icons-material";
import {getAllOwedTags} from "./Tags";
import {tableTag} from "./components/TagDataGrid";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {CustomToolbar} from "./components/CustomToolbar";
import {getTagSource} from "./tag-sources";

const columns: GridColDef[] = [
    {
        field: 'quantity', headerName: 'QTY', headerAlign: "center", width: 60, align: "right"
    },
    {
        field: 'item', headerName: 'Item', headerAlign: "left", width: 300, valueGetter: (params) => {
            if (params.row.type === "LCO_RITUAL_SCROLL") {
                return params.row.item + " (" + params.row.repNumber + ")"
            } else if (params.row.type === "MAGIC_ITEM") {
                return "Magic Item (" + params.row.repNumber + ")"
            } else if (params.row.type === "COIN") {
                return "Silver Coin"
            } else {
                return params.row.item
            }
        }
    },
    {
        field: 'source', headerName: 'Source', headerAlign: "left", width: 200, valueGetter: (params) => {
            return getTagSource(params.row.source)
        }
    },

]

export interface Reg {
    id: number,
    name: string,
    playerId: number,
    character: string,
    characterId: number,
    express?: boolean,
    player: { waivers: Waiver },
    checkedIn?: boolean,
    checkedInTime?: Date
    cabin?: { id: number, location: string }
    answers: { question: { text: string, number: number }, answer: string }[]
    payment?: any
    type: string
    outstanding?: { id: number, crafting: boolean }
}

export interface Waiver {
    id: number,
    general: boolean,
    photo: boolean,
    photoabstain: boolean,
    idChecked: boolean
}


const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

export const registrationLoader = async ({params}: LoaderFunctionArgs): Promise<{registration: Reg, paidParking: boolean} | null> => {
    if (typeof params.id !== "undefined" && typeof params.eventId !== "undefined") {
        const [registration, event] = await Promise.all([
            loadRegistrationForPlayer(parseInt(params.id), parseInt(params.eventId)),
            axios.get(`/api/events/${params.eventId}`).then(r => r.data)
        ])
        return {registration, paidParking: event?.paid_parking ?? true}
    } else {
        return null
    }
}

export async function loadRegistrationForPlayer(id: number, eventId: number): Promise<any> {
    const apiEndpoint = `/api/events/${eventId}/registration/` + id
    return await axios.get(apiEndpoint).then(({data}) => {
        let ret = data
        if (typeof ret.player.waivers === "undefined") {
            ret.player.waivers = {
                id: ret.player.playerId,
                general: false,
                photo: false,
                photoabstain: null,
                idChecked: false
            }
        }
        if (ret.player.waivers == null) {
            ret.player.waivers = {
                id: ret.player.playerId,
                general: false,
                photo: false,
                photoabstain: null,
                idChecked: false
            }
        }
        return data;
    })
}

export const registrationListLoader = async ({params}: LoaderFunctionArgs): Promise<{registrations: Reg[], paidParking: boolean} | null> => {
    if (typeof params.eventId === "undefined") return null
    const [registrations, event] = await Promise.all([
        loadRegistrationList(parseInt(params.eventId)),
        axios.get(`/api/events/${params.eventId}`).then(r => r.data)
    ])
    return {registrations, paidParking: event?.paid_parking ?? true}
}

export async function loadRegistrationList(eventId: number): Promise<any> {
    const apiEndpoint = `/api/events/${eventId}/registration`
    return await axios.get(apiEndpoint).then(({data}) => {
        return data;
    })
}

export async function checkPlayerIn(id: number, eventId: number): Promise<any> {
    const apiEndpoint = `/api/events/${eventId}/registration/` + id + "/checkin"
    return await axios.post(apiEndpoint).then(({data}) => {
        return data;
    })
}

export async function saveWaivers(w: Waiver, eventId: number) {
    const apiEndpoint = `/api/events/${eventId}/player/` + w.id + "/waivers";
    return await axios.post(apiEndpoint, w).then(({data}) => {
        return data
    })
}

export async function getPlayerOwedTags(id: number, eventId: number): Promise<tableTag[]> {
    const apiEndpoint = `/api/events/${eventId}/pcs/` + id + '/tag'
    return await axios.get(apiEndpoint).then(({data}) => {
        return data as tableTag[]
    })
}

export function isExpress(r: Reg) {
    let expressCheck = [r.player.waivers.idChecked, r.player.waivers.general, r.player.waivers.photo, !r.outstanding?.crafting]
    return expressCheck.every((e) => e === true)
}

export function owesPayment(r: Reg){
    if (r.type == "NPC"){
        return false
    }
    else if (r.payment == null){
        return true
    }else if (r.payment.event == false){
        return true
    }
}

export default function Registration() {
    const {eventId} = useParams<{eventId: string}>()
    const loaderData = useLoaderData() as {registration: Reg, paidParking: boolean}
    const [registration, setReg] = useState(loaderData.registration);
    const paidParking = loaderData.paidParking;
    const [loader, setLoader] = useState(0 as number);
    const [open, setOpen] = useState(false);
    const [owedTagsList, setOwedTagsList] = useState([] as tableTag[])

    useEffect(() => {
        if (eventId) getPlayerOwedTags(registration.playerId, parseInt(eventId)).then(data => setOwedTagsList(data))
    }, [])

    async function loadCard() {
        const apiEndpoint = `/api/events/${eventId}/card/` + registration.characterId
        try {
            const response = await axios.get(apiEndpoint, {
                responseType: 'blob',
                headers: {"Accept": "application/pdf", "Content-Type": "application/pdf"}
            });
            /*DOWNLOADS FILE FROM SERVER*/
            /*const href = window.URL.createObjectURL(response.data);

            const anchorElement = document.createElement('a');

            anchorElement.href = href;
            anchorElement.download = registration.character + ".pdf";

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);*/
            /*OPEN CARD IN BROWSER*/
            const file = new Blob([response.data], {type: "application/pdf"});
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
        } catch (error) {
            console.log(error);
        }
    }

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    function incrementLoader() {
        setLoader(loader + 1)
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        addItemToPreReg(e.target.repNumber.value, e.target.character.value)
        handleClose();
    }


    const waiverSigned = () => {
        console.log("SIGNED WAIVER")
        if (typeof registration.player.waivers !== "undefined") {
            registration.player.waivers.general = true
        }
        saveWaivers(registration.player.waivers as Waiver, parseInt(eventId!)).then(() => {
            setReg(registration)
        })
        incrementLoader()
    }

    const photoWaiverSigned = () => {
        console.log("SIGNED PHOTO WAIVER")
        if (typeof registration.player.waivers !== "undefined") {
            registration.player.waivers.photo = true
        }
        saveWaivers(registration.player.waivers as Waiver, parseInt(eventId!)).then(() => {
            setReg(registration)
        })
        incrementLoader()
    }

    const idChecked = () => {
        console.log("CHECKED ID")
        if (typeof registration.player.waivers !== "undefined") {
            registration.player.waivers.idChecked = true
        }
        saveWaivers(registration.player.waivers as Waiver, parseInt(eventId!)).then(() => {
            setReg(registration)
        })
        incrementLoader()
    }
    const navigate = useNavigate();

    const readyToCheckIn = () => {
        if ((registration.player.waivers !== null)) {
            let rdy = [registration.player.waivers.idChecked, registration.player.waivers.general, registration.player.waivers.photo, !registration.checkedIn]
            return rdy.every((e) => e === true);
        } else {
            return false
        }

    }
    const checkIn = async () => {
        console.log("CHECKING IN")
        if (readyToCheckIn()) {
            registration.checkedIn = true;
            registration.checkedInTime = new Date();
        }
        setReg(registration)
        incrementLoader()
        await checkPlayerIn(registration.playerId, parseInt(eventId!)).then(() => {
                navigate("..")
            }
        )

    }

    useEffect(() => {
        console.log(registration)
    }, []);

    const meal = () => {
        if (registration.payment !== null) {
            if (registration.payment.meal) {
                return true
            }
        }
        return false
    }

    const parking = () => {
        if (registration.payment !== null) {
            if (registration.payment.parking) {
                return true
            }
        }
        return false
    }

    const outstandingCrafting = () => {
        if (registration.outstanding !== null) {
            return registration.outstanding?.crafting
        }
    }

    const npc = () => {
        return (registration.type == "NPC")
    }

    const pc = () => {
        return (registration.type == "PLAYER")
    }

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography>{registration.character} ({registration.name})</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <TextField fullWidth id='cabinAssignment' label="Cabin Assignment"
                               disabled={true} sx={{color: "black"}}
                               defaultValue={registration.cabin?.location}/>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{height: '100%', p: 1}}>
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={3}
                    >
                        {(isExpress(registration)) ? <Box><FlashOn htmlColor={"yellow"}/></Box> : null}
                        {(outstandingCrafting()) ? <Box><Construction htmlColor={"red"}/></Box> : null}
                        {(owesPayment(registration)) ? <Box><PointOfSale htmlColor={"red"}/></Box> : null}
                        {(paidParking && parking()) ? <Box><DirectionsCar htmlColor={"green"}/></Box> : null}
                        {(meal()) ? <Box><LocalDining htmlColor={"green"}/></Box> : null}
                        {(npc()) ? <Box><Adb htmlColor={"green"}/></Box> : null}
                    </Stack>
                </Paper>
            </Grid>
            <Grid container item spacing={1}>
                <ActionButton action={waiverSigned} name="Signed Waiver"
                              disabled={registration.player.waivers.general}
                              xs={12} sm={6} md={4}
                              color={"error"}/>
                <ActionButton action={photoWaiverSigned} name="Signed Photo Waiver"
                              disabled={registration.player.waivers.photo}
                              xs={12} sm={6} md={4} color={"error"}/>
                <ActionButton action={idChecked} name="ID Checked"
                              disabled={registration.player.waivers.idChecked}
                              xs={12} sm={6} md={4}
                              color={"error"}/>
                {(pc()) ? <ActionButton action={handleOpen} name="Add Magic Item" disabled={true} xs={12} sm={6} md={4}
                                        color={"primary"}/> : null}
                {(pc()) ? <ActionButton action={loadCard} name="Reprint Card" disabled={true} xs={12} sm={6} md={4}
                                        color={"primary"}/> : null}
                <ActionButton action={checkIn}
                              name={(registration.checkedIn) ? "Player Checked In" : "CHECK IN " + registration.type}
                              disabled={!readyToCheckIn()} xs={12} sm={12} md={12}
                              color={"success"}/>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1, mt: 2}}>
                    <Typography align={"center"}>Owed Tags</Typography>
                    <DataGrid slots={{toolbar: CustomToolbar}}
                              localeText={{
                                  toolbarColumns: "",
                                  toolbarFilters: "",
                                  toolbarDensity: "",
                                  toolbarExport: ""
                              }}
                              rows={owedTagsList}
                              disableRowSelectionOnClick
                              columns={columns}
                              density={"compact"}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1, mt: 2}}>
                    <Typography align={"center"}>Registration Questions</Typography>
                    {[...registration.answers].sort((a, b) => a.question.number - b.question.number).map((answer) => {
                        return <React.Fragment><Paper elevation={2} sx={{p: 1, m: 1}}><Typography variant={"body1"}
                                                                                                  fontSize={".75rem"}
                                                                                                  sx={{
                                                                                                      color: 'primary.main',
                                                                                                      overflowWrap: 'break-word'
                                                                                                  }}>{answer.question.text}</Typography><Paper
                            elevation={2} sx={{p: .25, m: .25}}><Typography
                            variant={"body2"} sx={{whiteSpace: 'pre-wrap'}}>{answer.answer}</Typography></Paper></Paper></React.Fragment>
                    })}
                </Paper>
            </Grid>
        </Grid>
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Box>
                    <Typography variant={"h6"} component={"h2"}>Add Item to Reg</Typography>
                </Box>
                <Stack spacing={3} component="form" autoComplete="off" onSubmit={handleSubmit}>
                    <TextField required id="character" label="Charcter" value={registration.character} disabled/>
                    <TextField required id="repNumber" label="Rep Number"/>
                    <Button variant="contained" type="submit" onClick={() => {
                    }}>Submit</Button>
                </Stack>
            </Box>
        </Modal>
    </React.Fragment>
}

function ActionButton(params: {
    action: Function,
    name: string,
    disabled: boolean,
    xs: number,
    sm: number,
    md: number,
    color: string
}) {
    // @ts-ignore
    return <Grid item xs={params.xs} sm={params.sm} md={params.md} sx={{height: "70px"}}><Button
        disabled={params.disabled} color={params.color}
        variant="contained"
        sx={{
            height: "100%",
            width: "100%",
            stretch: "100%"
        }}
        onClick={() => {
            params.action()
        }}><Typography>{params.name}</Typography></Button></Grid>
}